import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Package, Truck, MapPin, FileText, LogOut, PlusCircle, Loader2, Copy, Inbox,
  CheckCircle2, Circle, CalendarClock, Route,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  completed: { label: 'Completato', cls: 'bg-green-100 text-green-700' },
  processing: { label: 'In elaborazione', cls: 'bg-amber-100 text-amber-700' },
  'on-hold': { label: 'In attesa', cls: 'bg-slate-100 text-slate-600' },
  pending: { label: 'In sospeso', cls: 'bg-slate-100 text-slate-600' },
  cancelled: { label: 'Annullato', cls: 'bg-red-100 text-red-700' },
  refunded: { label: 'Rimborsato', cls: 'bg-red-100 text-red-700' },
  failed: { label: 'Fallito', cls: 'bg-red-100 text-red-700' },
  da_scont: { label: 'Da scontrinare', cls: 'bg-blue-100 text-blue-700' },
  fatturare: { label: 'Da fatturare', cls: 'bg-indigo-100 text-indigo-700' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>{s.label}</span>;
};

const MyShipments = () => {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem('occ_token'));
  const [customer] = useState(() => {
    try { return JSON.parse(localStorage.getItem('occ_customer') || 'null'); } catch { return null; }
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [labelLoading, setLabelLoading] = useState(null);
  const [tracking, setTracking] = useState({});   // orderId -> {loading, open, data}

  const toggleTracking = async (orderId) => {
    const cur = tracking[orderId];
    if (cur && cur.data) {
      setTracking((t) => ({ ...t, [orderId]: { ...cur, open: !cur.open } }));
      return;
    }
    setTracking((t) => ({ ...t, [orderId]: { loading: true, open: true } }));
    try {
      const res = await axios.get(`${API}/occasional/orders/${orderId}/tracking`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTracking((t) => ({ ...t, [orderId]: { loading: false, open: true, data: res.data } }));
    } catch (e) {
      toast.error('Impossibile caricare lo stato spedizione');
      setTracking((t) => ({ ...t, [orderId]: { loading: false, open: false } }));
    }
  };

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/occasional/customer/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data.orders || []);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('occ_token');
        localStorage.removeItem('occ_customer');
        toast.error('Sessione scaduta, effettua di nuovo l\'accesso');
        navigate('/spedizione');
      } else {
        toast.error('Impossibile caricare le spedizioni');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const logout = () => {
    localStorage.removeItem('occ_token');
    localStorage.removeItem('occ_customer');
    navigate('/spedizione');
  };

  const downloadLabel = async (orderId) => {
    setLabelLoading(orderId);
    try {
      const res = await axios.get(`${API}/occasional/orders/${orderId}/label`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.type === 'url') {
        window.open(res.data.url, '_blank');
      } else if (res.data.type === 'pdf') {
        const bin = atob(res.data.data_base64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        const blob = new Blob([arr], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Etichetta non disponibile');
    } finally {
      setLabelLoading(null);
    }
  };

  const copyTracking = (t) => {
    navigator.clipboard?.writeText(t);
    toast.success('Tracking copiato');
  };

  const Header = () => (
    <header className="bg-[#0a0a1a] text-white">
      <div className="max-w-4xl mx-auto px-4 py-5 flex items-center gap-3">
        <Truck className="w-6 h-6 text-blue-400" />
        <div>
          <h1 className="text-lg font-semibold">Le mie spedizioni</h1>
          {customer?.email && <p className="text-xs text-slate-400">{customer.email}</p>}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <button onClick={() => navigate('/profilo')} className="text-sm text-blue-300 hover:text-white transition-colors" data-testid="profile-nav">
            I miei dati
          </button>
          <button onClick={() => navigate('/spedizione')} className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="new-shipment-nav">
            + Nuova spedizione
          </button>
          {token && (
            <button onClick={logout} className="text-sm text-slate-300 hover:text-white flex items-center gap-1 transition-colors" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          )}
        </div>
      </div>
    </header>
  );

  // Not logged in
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="not-logged-in">
          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-5">
            <Package className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800">Accedi per vedere le tue spedizioni</h2>
          <p className="text-slate-500 mt-2 text-sm">Tieni traccia dei tuoi pacchi e scarica le lettere di vettura.</p>
          <Button className="mt-6" onClick={() => navigate('/spedizione')} data-testid="go-login-btn">
            Accedi o registrati
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carico le tue spedizioni...
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-5">
              <Inbox className="w-8 h-8 text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800">Nessuna spedizione ancora</h2>
            <p className="text-slate-500 mt-2 text-sm">Spedisci il tuo primo pacco in pochi minuti.</p>
            <Button className="mt-6" onClick={() => navigate('/spedizione')} data-testid="first-shipment-btn">
              <PlusCircle className="w-4 h-4 mr-2" /> Spedisci ora
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <Card key={o.id} data-testid={`order-card-${o.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-slate-800">Ordine #{o.number}</span>
                        <StatusBadge status={o.status} />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {o.date ? new Date(o.date).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-slate-800">€ {Number(o.total).toFixed(2)}</div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{o.destinatario || '—'}{o.citta ? ` · ${o.citta}` : ''}</span>
                  </div>

                  {o.tracking && (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <Truck className="w-4 h-4 text-blue-500" />
                      <span className="text-slate-500">Tracking:</span>
                      <span className="font-medium text-slate-800" data-testid={`tracking-${o.id}`}>{o.tracking}</span>
                      <button onClick={() => copyTracking(o.tracking)} className="text-slate-400 hover:text-blue-500" title="Copia">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {o.pickup_code && (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <CalendarClock className="w-4 h-4 text-emerald-500" />
                      <span className="text-slate-500">Ritiro:</span>
                      <span className="font-medium text-slate-800">{o.pickup_date || 'prenotato'}</span>
                      <span className="text-xs text-slate-400">cod. {o.pickup_code}</span>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-3">
                    {o.has_label ? (
                      <Button variant="outline" size="sm" onClick={() => downloadLabel(o.id)}
                        disabled={labelLoading === o.id} data-testid={`label-btn-${o.id}`}>
                        {labelLoading === o.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                        Lettera di vettura
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Loader2 className="w-3.5 h-3.5" /> Etichetta in preparazione
                      </span>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => toggleTracking(o.id)} data-testid={`track-btn-${o.id}`}>
                      <Route className="w-4 h-4 mr-2" /> Stato spedizione
                    </Button>
                  </div>

                  {tracking[o.id]?.open && (
                    <div className="mt-4 rounded-lg bg-slate-50 p-4" data-testid={`tracking-timeline-${o.id}`}>
                      {tracking[o.id].loading ? (
                        <div className="flex items-center text-sm text-slate-500"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carico...</div>
                      ) : tracking[o.id].data ? (
                        <>
                          <div className="text-sm font-semibold text-slate-800 mb-3">{tracking[o.id].data.status_label}</div>
                          <ol className="space-y-2">
                            {tracking[o.id].data.events.map((ev) => (
                              <li key={ev.key} className="flex items-center gap-2 text-sm">
                                {ev.done
                                  ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                                  : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
                                <span className={ev.done ? 'text-slate-700' : 'text-slate-400'}>{ev.label}</span>
                              </li>
                            ))}
                          </ol>
                        </>
                      ) : null}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyShipments;
